import React, { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Tooltip } from "../ui/tooltip";
import { InfoIcon } from "lucide-react";

interface SimpleAgentRef { id: number; agent_name: string }

interface FormFieldModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (field: { name: string; description: string; type: string; updateRules?: string; readScope: string; writeScope: string; readAgents?: number[]; writeAgents?: number[] }) => void;
  editingField?: { name: string; description: string; type: string; updateRules?: string; readScope?: string; writeScope?: string; readAgents?: number[]; writeAgents?: number[] } | null;
  subagents?: SimpleAgentRef[]; // direct subagents to choose from when scope is custom
}

const FormFieldModal: React.FC<FormFieldModalProps> = ({
  isOpen,
  onClose,
  onSave,
  editingField,
  subagents
}) => {
  const [name, setName] = useState<string>("");
  const [type, setType] = useState<string>("string");
  const [description, setDescription] = useState<string>("");
  const [updateRules, setUpdateRules] = useState<string>("");
  const [readScope, setReadScope] = useState<string>("private");
  const [writeScope, setWriteScope] = useState<string>("private");
  const [readAgents, setReadAgents] = useState<number[]>([]);
  const [writeAgents, setWriteAgents] = useState<number[]>([]);

  // Initialize form when modal opens or editingField changes
  useEffect(() => {
    if (isOpen) {
      if (editingField) {
        setName(editingField.name || "");
        setType(editingField.type || "string");
        setDescription(editingField.description || "");
        setUpdateRules(editingField.updateRules || "");
        setReadScope(editingField.readScope || "public");
        setWriteScope(editingField.writeScope || "public");
        setReadAgents(Array.isArray(editingField.readAgents) ? editingField.readAgents : []);
        setWriteAgents(Array.isArray(editingField.writeAgents) ? editingField.writeAgents : []);
      } else {
        setName("");
        setType("string");
        setDescription("");
        setUpdateRules("");
        setReadScope("private");
        setWriteScope("private");
        setReadAgents([]);
        setWriteAgents([]);
      }
    }
  }, [isOpen, editingField]);

  const handleSave = () => {
    if (!name.trim()) return;
    
    onSave({
      name: name.trim(),
      type,
      description: description.trim(),
      updateRules: updateRules.trim(),
      readScope,
      writeScope,
      readAgents,
      writeAgents
    });

    // Reset form
    setName("");
    setType("string");
    setDescription("");
    setUpdateRules("");
    setReadScope("private");
    setWriteScope("private");
    setReadAgents([]);
    setWriteAgents([]);
  };

  const handleClose = () => {
    setName("");
    setType("string");
    setDescription("");
    setUpdateRules("");
    setReadScope("private");
    setWriteScope("private");
    setReadAgents([]);
    setWriteAgents([]);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open) handleClose(); }}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] flex flex-col">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle>{editingField ? "Edit Form Field" : "Add Form Field"}</DialogTitle>
          <DialogDescription>
            Define a field that your agent can collect during conversations.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4 overflow-y-auto flex-1">
          <div className="space-y-2">
            <Label htmlFor="field-name">Field Name</Label>
            <Input
              id="field-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., user_email"
              className="font-mono"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="field-type">Type</Label>
            <Select 
              value={type} 
              onValueChange={(value) => setType(value)}
            >
              <SelectTrigger id="field-type">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="string">String</SelectItem>
                <SelectItem value="number">Number</SelectItem>
                <SelectItem value="boolean">Boolean</SelectItem>
                <SelectItem value="date">Date</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          
          <div className="space-y-2">
            <Label htmlFor="field-description">Description</Label>
            <Textarea
              id="field-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Describe what this field is for..."
              rows={3}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="field-update-rules">Update Rules</Label>
            <Textarea
              id="field-update-rules"
              value={updateRules}
              onChange={(e) => setUpdateRules(e.target.value)}
              placeholder="Define when and how this field should be updated..."
              rows={3}
            />
          </div>
        
          {/* Permissions Section */}
          <div className="border-t border-border pt-6 pb-4 space-y-4">
          <div>
            <h3 className="text-base font-gc-med text-foreground mb-1">Permissions</h3>
            <p className="text-sm text-muted-foreground">Control which subagents can access this field</p>
          </div>
          
          <div className="space-y-3">
            <Label>Scope</Label>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <div className="flex items-center gap-1.5">
                  <span className="text-sm text-muted-foreground">Read</span>
                  <Tooltip 
                    content="Controls which subagents can view this field's value. Public: all subagents. Private: only this agent. Custom: selected subagents only."
                    side="top"
                    immediate={true}
                  >
                    <InfoIcon className="w-3.5 h-3.5 text-muted-foreground/60 cursor-help" />
                  </Tooltip>
                </div>
                <Select 
                  value={readScope} 
                  onValueChange={(value) => {
                    setReadScope(value);
                    if (value === "private" && writeScope !== "private") {
                      setWriteScope("private");
                      setWriteAgents([]);
                    }
                    if (value !== "custom") {
                      setReadAgents([]);
                    }
                  }}
                >
                  <SelectTrigger id="field-read-scope">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="public">Public</SelectItem>
                    <SelectItem value="private">Private</SelectItem>
                    <SelectItem value="custom">Custom</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <div className="flex items-center gap-1.5">
                  <span className="text-sm text-muted-foreground">Write</span>
                  <Tooltip 
                    content="Controls which subagents can update this field's value. Public: all subagents. Private: only this agent. Custom: selected subagents only. Note: write access requires read access."
                    side="top"
                    immediate={true}
                  >
                    <InfoIcon className="w-3.5 h-3.5 text-muted-foreground/60 cursor-help" />
                  </Tooltip>
                </div>
                <Select 
                  value={writeScope} 
                  onValueChange={(value) => {
                    if ((value === "public" || value === "custom") && readScope === "private") {
                      setReadScope("public");
                    }
                    setWriteScope(value);
                    if (value !== "custom") {
                      setWriteAgents([]);
                    }
                  }}
                >
                  <SelectTrigger id="field-write-scope">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="public">Public</SelectItem>
                    <SelectItem value="private">Private</SelectItem>
                    <SelectItem value="custom">Custom</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          {/* Subagent Permissions - only show if subagents exist */}
          {(subagents || []).length === 0 ? (
            <div className="text-center py-2">
              <span className="text-sm text-muted-foreground">No subagents found</span>
            </div>
          ) : (
            <div className="space-y-2">
              <div className="flex items-center gap-1.5">
                <Label>Subagent Permissions</Label>
                <Tooltip 
                  content="Preview and configure which direct subagents have read/write access to this field. Public scope grants access to all subagents. Private restricts to this agent only. Custom lets you select specific subagents."
                  side="top"
                  immediate={true}
                >
                  <InfoIcon className="w-3.5 h-3.5 text-muted-foreground/60 cursor-help" />
                </Tooltip>
              </div>
              <div className="border rounded">
                <div className="grid grid-cols-6 px-3 py-2 text-xs text-muted-foreground border-b items-start">
                  <div className="col-span-4">Subagent</div>
                  <div className="col-span-1">
                    <div>Read</div>
                    {(readScope === "custom" || writeScope === "custom") && readScope === "custom" && (
                      <div className="flex gap-3 mt-1">
                        <button
                          type="button"
                          className="underline hover:no-underline"
                          onClick={() => setReadAgents(((subagents || []) as SimpleAgentRef[]).map((sa: SimpleAgentRef) => sa.id))}
                        >
                          All
                        </button>
                        <button
                          type="button"
                          className="underline hover:no-underline"
                          onClick={() => {
                            setReadAgents([]);
                            setWriteAgents([]);
                          }}
                        >
                          None
                        </button>
                      </div>
                    )}
                  </div>
                  <div className="col-span-1">
                    <div>Write</div>
                    {(readScope === "custom" || writeScope === "custom") && writeScope === "custom" && (
                      <div className="flex gap-3 mt-1">
                        <button
                          type="button"
                          className="underline hover:no-underline"
                          onClick={() => {
                            const allIds = ((subagents || []) as SimpleAgentRef[]).map((sa: SimpleAgentRef) => sa.id);
                            setReadAgents((prev) => Array.from(new Set([...(prev || []), ...allIds])));
                            setWriteAgents(allIds);
                          }}
                        >
                          All
                        </button>
                        <button
                          type="button"
                          className="underline hover:no-underline"
                          onClick={() => setWriteAgents([])}
                        >
                          None
                        </button>
                      </div>
                    )}
                  </div>
                </div>
                <div className="max-h-48 overflow-auto">
                  {(subagents || []).map((sa: SimpleAgentRef) => {
                    const canRead = readScope === "public" || (readScope === "custom" ? readAgents.includes(sa.id) : false);
                    const baseCanWrite = writeScope === "public" || (writeScope === "custom" ? writeAgents.includes(sa.id) : false);
                    const canWrite = canRead ? baseCanWrite : false;
                    return (
                      <div key={`preview-${sa.id}`} className="grid grid-cols-6 px-3 py-2 text-sm items-center">
                        <div className="col-span-4 truncate" title={sa.agent_name}>{sa.agent_name}</div>
                        <div className="col-span-1">
                          <input
                            type="checkbox"
                            checked={canRead}
                            onChange={(e) => {
                              const checked = e.target.checked;
                              if (readScope !== "custom") return;
                              setReadAgents((prev) => {
                                const next = checked ? Array.from(new Set([...(prev || []), sa.id])) : (prev || []).filter((id) => id !== sa.id);
                                if (!checked) setWriteAgents((wprev) => (wprev || []).filter((id) => id !== sa.id));
                                return next;
                              });
                            }}
                            disabled={readScope !== "custom"}
                          />
                        </div>
                        <div className="col-span-1">
                          <input
                            type="checkbox"
                            checked={canWrite}
                            onChange={(e) => {
                              const checked = e.target.checked;
                              if (writeScope !== "custom") return;
                              setWriteAgents((prev) => {
                                let next = prev || [];
                                if (checked) {
                                  setReadAgents((rprev) => Array.from(new Set([...(rprev || []), sa.id])));
                                  next = Array.from(new Set([...(prev || []), sa.id]));
                                } else {
                                  next = (prev || []).filter((id) => id !== sa.id);
                                }
                                return next;
                              });
                            }}
                            disabled={writeScope !== "custom" || (!canRead && readScope !== "public")}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
          </div>
        </div>
        <div className="flex justify-end gap-2 flex-shrink-0 pt-4 border-t border-border">
          <Button variant="outline" onClick={handleClose}>
            Cancel
          </Button>
          <Button 
            onClick={handleSave}
            disabled={!name.trim()}
          >
            {editingField ? "Save Changes" : "Add Field"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default FormFieldModal;

